import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { ritentaSoloLeLetture, type OpzioniRitentativo } from '@/lib/ritentaSoloLeLetture';
import { guardiaOspite } from '@/services/guardiaOspite';
import { getQduelsAccessToken } from '@/lib/logto';
import type {
  ChallengeDTO,
  CreateChallengeRequest,
  CrProfileDTO,
  ListChallengesQuery,
  ListMyTournamentsQuery,
  ListOpenTournamentsQuery,
  MatchDTO,
  MatchStatsDTO,
  PlayerByCrTagDTO,
  PlayerLinkDTO,
  QduelsMyTournamentsResponse,
  QduelsOpenTournamentsPage,
  QduelsTournamentDetail,
  QduelsTournamentJoinResult,
  QduelsTournamentLeaveResult,
  QduelsQueueCheckinResult,
  QduelsTournamentCheckinResult,
  QduelsTournamentParticipantsPage,
  QduelsTournamentSlotsPage,
  QduelsTournamentStandings,
  QueueEnterRequest,
  QueueEntry,
  QueueStatus,
  QduelsRating,
  QduelsUserLeague,
  QduelsLadder,
  VerifyTokenRequest,
  VerifyTokenResponse,
} from '@/types/qduels';

/**
 * QDuels API client. Talks to qduels-api at `/v1/*`.
 *
 * Auth: Logto Bearer JWT with audience `https://qduels.qlash.gg/api`, minted
 * by getQduelsAccessToken() through the shared serialized refresh-token
 * redemption (same pattern as qriots/qbot/qsocials). qduels-api validates the
 * JWT `aud` against exactly that audience and the issuer against
 * `https://account.qlash.gg/oidc`.
 *
 * ⚠️ Logto app grant required: minting a token for the qduels audience only
 * works if the TMA's Logto application has the "Qlash API QDuels" API resource
 * granted server-side (Logto console → Applications → <TMA app> → API
 * resources). If that grant is missing, getQduelsAccessToken() resolves to
 * null (refresh grant → HTTP 400), no Authorization header is attached, and
 * qduels answers 401 `missing_bearer_token`. This is the one config step the
 * Mini App can't self-provision — see the report / CLAUDE.md note.
 *
 * Dev: env.qduelsApiUrl = '/qduels-api', Vite proxy forwards to qduels.qlash.gg.
 * Prod: env.qduelsApiUrl = 'https://qduels.qlash.gg'.
 *
 * Unlike qbot/qsocials, qduels responses are NOT wrapped in a `{ data }` /
 * `{ success }` envelope — `POST /v1/challenges` returns the challenge object
 * directly with HTTP 201, and errors return `{ error: "<code>" }`.
 */
/**
 * 🔴 LA SEZIONE SPENTA SU QUESTO AMBIENTE — e il perche' e' una misura, non una
 * preferenza.
 * [GO di Luca, 2026-09-07 21:16:16Z, L-565: «(a) Spegni: "non disponibile qui"».]
 *
 * `env.qduelsApiUrl` non ha un valore di staging perche' **uno staging di qduels
 * non esiste** [M or56, 07/09: `stg-qduels.qlash.gg` e `qduels-stg.qlash.gg` non
 * risolvono; la produzione risponde `200` — che e' il controllo positivo della
 * sonda]. ⇒ senza questa guardia, una build di **staging** chiama la
 * **produzione**.
 *
 * ⚠️ E oggi quelle chiamate **non funzionano gia'**: la resource `qduels` non e'
 * fra le `STAGING_RESOURCES` dell'app, quindi su staging non si conia nessun
 * token per quell'audience e ogni chiamata torna **401** (misurato: `401 GET
 * /v1/rating` su due profili). ⇒ spegnere **non toglie niente che funzioni**:
 * toglie una chiamata alla produzione e una bugia a schermo.
 *
 * 🔑 PERCHE' IL PUNTO DI STROZZATURA E' QUI E NON NELLE SCHERMATE. I consumatori
 * sono **sedici file** (Amici, profili, duelli, coda, tre pannelli della Hub,
 * collegamento CR…): una guardia per schermata avrebbe lasciato passare la prima
 * che qualcuno dimentica, e *il difetto che ne esce e' una chiamata alla
 * produzione, cioe' esattamente cio' che questa riga esiste per impedire*. Qui
 * invece **nessuna chiamata parte**, qualunque schermata la chieda.
 * ⇒ Le schermate restano libere di dire la frase giusta (`reachable: false` di
 * `listVerdict`, il caso «non posso chiedere» che il repo ha gia'), e chi non la
 * dice ancora mostra il proprio errore — **come oggi col 401**, non peggio.
 *
 * ⛔ L'errore e' NOMINATO (`qduels_disabled`) e non un 4xx finto: un codice HTTP
 * inventato si confonderebbe con una risposta del server, e chi indaga cerchera'
 * un server che non e' stato chiamato.
 */
const qduelsDisabledQuery: BaseQueryFn<unknown, unknown, unknown, OpzioniRitentativo> = async () => ({
  error: { status: 'CUSTOM_ERROR' as const, error: 'qduels_disabled', data: undefined },
});

export const qduelsApi = createApi({
  reducerPath: 'qduelsApi',
  baseQuery: guardiaOspite('qduels', !env.featureQduelsEnabled
    ? qduelsDisabledQuery
    // ⛔ Una SCRITTURA (mutation, o un metodo che non e' GET) non si ritenta MAI — salvo le TRE che dichiarano
    // `extraOptions: { idempotente: true }` (`queueLeave`, `checkInQueue`, `checkInTournament`: ciascuna con la sua
    // ragione e il suo costo, sull'endpoint): un `createChallenge` o un
    // `acceptChallenge` arrivati al server con la risposta persa verrebbero RIPETUTI (una seconda sfida, una seconda
    // partita). Una LETTURA si ritenta UNA volta, solo su rete/timeout/5xx; un 4xx e' deterministico (una sfida che
    // fallisce con defender_not_linked / cannot_challenge_self / 401 non guarisce ritentando: si ritarda solo il toast).
    // La regola sta in `lib/ritentaSoloLeLetture.ts`, provata in `tests/ritentativi-altri-client.test.tsx`.
    : ritentaSoloLeLetture(
    fetchBaseQuery({
      baseUrl: env.qduelsApiUrl,
      prepareHeaders: async (headers) => {
        const token = await getQduelsAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        headers.set('Accept', 'application/json');
        headers.set('Content-Type', 'application/json');
        return headers;
      },
    }),
  )),
  // 'PlayerLink' tracks the caller's CR account link (GET /v1/players/me).
  // A successful verify-token invalidates it so getMyLink refetches and any
  // gated "Sfida" buttons flip from "link first" to enabled without a reload.
  // 'Challenges' tracks the caller's challenge lists; create/accept/decline/
  // cancel invalidate it so the Sfide tab re-fetches.
  // 'Queue' tracks the caller's matchmaking-queue status; enter/leave invalidate
  // it so a status refetch reflects the new in-queue state immediately.
  // NB: 'Tournaments' collides by NAME with the main (Laravel) api's tag, but
  // tag namespaces are per-createApi instance — the two never invalidate each
  // other. Same isolation qchessApi already relies on.
  tagTypes: ['PlayerLink', 'Challenges', 'Queue', 'Tournaments', 'Rating', 'Ladder'],
  endpoints: (builder) => ({
    /**
     * Create a 1v1 duel challenge against a friend. The challenger is derived
     * from the Bearer JWT server-side; the body carries the defender's Logto
     * id + match settings. Returns the created challenge (status `pending`).
     */
    createChallenge: builder.mutation<ChallengeDTO, CreateChallengeRequest>({
      query: (body) => ({
        url: '/v1/challenges',
        method: 'POST',
        body,
      }),
      // Refresh the caller's challenge lists so the new outgoing challenge
      // shows up in the Sfide tab immediately.
      invalidatesTags: ['Challenges'],
    }),

    /**
     * List the caller's challenges. `role: 'defender'` = incoming, `role:
     * 'challenger'` = outgoing; omit `role` to get both sides. Optional
     * `status` filter + `limit` (server caps at 100). Returns a bare array
     * (no envelope), newest-updated first.
     */
    listChallenges: builder.query<ChallengeDTO[], ListChallengesQuery | void>({
      query: (params) => ({
        url: '/v1/challenges',
        params: params
          ? {
              ...(params.role ? { role: params.role } : {}),
              ...(params.status ? { status: params.status } : {}),
              ...(params.limit != null ? { limit: params.limit } : {}),
            }
          : undefined,
      }),
      providesTags: ['Challenges'],
    }),

    /** Single challenge by id (GET /v1/challenges/:id). Used to poll status. */
    getChallenge: builder.query<ChallengeDTO, string>({
      query: (challengeId) => `/v1/challenges/${encodeURIComponent(challengeId)}`,
      providesTags: ['Challenges'],
    }),

    /**
     * Defender accepts a pending challenge. On success qduels flips it to
     * `in_progress` and creates the hosted Supercell match — both players then
     * receive the in-game invite. 403 `not_the_defender`, 409
     * `cannot_accept_<status>` on a stale/expired challenge.
     */
    acceptChallenge: builder.mutation<ChallengeDTO, string>({
      query: (challengeId) => ({
        url: `/v1/challenges/${encodeURIComponent(challengeId)}/accept`,
        method: 'POST',
      }),
      invalidatesTags: ['Challenges'],
    }),

    /** Defender declines a pending challenge (notifies the challenger). */
    declineChallenge: builder.mutation<ChallengeDTO, string>({
      query: (challengeId) => ({
        url: `/v1/challenges/${encodeURIComponent(challengeId)}/decline`,
        method: 'POST',
      }),
      invalidatesTags: ['Challenges'],
    }),

    /**
     * Challenger cancels their own challenge. While `pending` it's a plain
     * cancel; mid-series it counts as a forfeit (defender wins). 403
     * `not_the_challenger`.
     */
    cancelChallenge: builder.mutation<ChallengeDTO, string>({
      query: (challengeId) => ({
        url: `/v1/challenges/${encodeURIComponent(challengeId)}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['Challenges'],
    }),

    /**
     * The caller's current Clash Royale link (GET /v1/players/me).
     *
     * 404 `{ error: "no_linked_player" }` is the "not linked" state, NOT a
     * hard failure. RTK Query surfaces any non-2xx as `error`, so callers must
     * read `error.status === 404` as "not linked yet" (see `isNotLinkedError`
     * in qduelsErrors). We deliberately do NOT swallow the 404 into `data`
     * here: keeping it on the error channel lets the gate distinguish "not
     * linked" (404) from a transient auth/network failure that should not
     * silently behave like "not linked".
     */
    getMyLink: builder.query<PlayerLinkDTO, void>({
      query: () => '/v1/players/me',
      providesTags: ['PlayerLink'],
    }),

    /**
     * Link a Clash Royale account by verifying ownership via the in-game API
     * token (POST /v1/players/verify-token). On `verified: true` the caller's
     * logtoUserId is linked to `playerTag`; we invalidate 'PlayerLink' so the
     * link status refetches everywhere.
     *
     * Error cases the caller maps to copy: 422 `{ verified: false }` (token
     * wrong/expired), 409 `player_tag_already_linked`, 400 `invalid_player_tag`.
     */
    verifyToken: builder.mutation<VerifyTokenResponse, VerifyTokenRequest>({
      query: (body) => ({
        url: '/v1/players/verify-token',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PlayerLink'],
    }),

    /**
     * Cached Clash Royale profile for a tag (GET /v1/players/:playerTag/profile).
     * Optional — used only to show the linked account's name + headline stats.
     */
    playerProfile: builder.query<CrProfileDTO, string>({
      query: (playerTag) => `/v1/players/${encodeURIComponent(playerTag)}/profile`,
      keepUnusedDataFor: 600,
    }),

    /**
     * Identity bridge (CR side): resolve a Clash Royale tag → the owner's
     * Logto `sub` (GET /v1/players/by-cr-tag/:tag). Lets CR surfaces (battle
     * opponent, public CR profile) light up follow/add-friend buttons for
     * opponents who linked their CR account in qduels.
     *
     * 404 `{ error: "player_not_found" }` (tag not linked) and 400
     * `{ error: "invalid_player_tag" }` are NOT hard failures — callers read
     * `error.status === 404`/`400` as "not resolved" and render no social
     * buttons (just the existing "View CR profile" affordance). The 4xx stays
     * on the error channel; the retry baseQuery already skips retrying 4xx.
     */
    getPlayerByCrTag: builder.query<PlayerByCrTagDTO, string>({
      query: (tag) => `/v1/players/by-cr-tag/${encodeURIComponent(tag)}`,
      keepUnusedDataFor: 600,
    }),

    // ─── Open matchmaking queue ──────────────────────────────────────────

    /**
     * Join the open 1v1 matchmaking queue (POST /v1/queue/enter). The caller is
     * derived from the Bearer JWT; the body carries the fixed pool. v1 always
     * sends `gameMode: 'REGULAR'` + `format: 'bo1'` (single fixed pool, single
     * game — no picker). Returns the created/updated `QueueEntry` (HTTP 201).
     * Re-entering while already queued is idempotent server-side.
     *
     * Errors mapped to UI copy via classifyQduelsQueueError: `player_not_linked`
     * (400) → /duels/link, `not_eligible` (403) → eligibility message,
     * `already_in_match` (409) → open Clash Royale.
     */
    queueEnter: builder.mutation<QueueEntry, QueueEnterRequest>({
      query: (body) => ({
        url: '/v1/queue/enter',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Queue'],
    }),

    /**
     * Leave the matchmaking queue (POST /v1/queue/leave) → `{ left }`. Called on
     * the QueueSheet's close / cancel / timeout to avoid ghost queue entries
     * (the backend has no TTL/heartbeat yet — see the spec's backend follow-up).
     */
    queueLeave: builder.mutation<{ left: boolean }, void>({
      query: () => ({
        url: '/v1/queue/leave',
        method: 'POST',
      }),
      // 🔴 ECCEZIONE DICHIARATA (`lib/ritentaSoloLeLetture.ts`): una scrittura che si ritenta UNA volta, su rete/timeout/5xx.
      // PERCHE': e' lanciata senza attesa alla chiusura del foglio (`QueueSheet.leaveQueue`, la promessa e' buttata) e una
      // `leave` persa lascia una voce ABBINABILE fino al reaper del server (ogni 15 s, `QUEUE_TTL_SEC` 45 di default; 180 in
      // produzione secondo lo STATUS.md di QDuels, non riletto dall'host): con il check-in di coda acceso l'avversario
      // perde fino a 2 minuti e chi era «uscito» prende il blocco no-show di 5 minuti.
      // ⛔ COSTO: la `leave` agisce sull'UTENTE, non su una voce (nessun id): una seconda `leave` che arriva DOPO un rientro
      // toglie dalla coda chi e' appena rientrato. Esiste gia' cosi' su `main`. Il seguito e' annullare la `leave` pendente
      // quando il foglio rientra in coda; non e' fatto qui.
      // Fonte dei fatti sul server: clone locale di QDuels @de2f7a79, letto il 30/09/2026 (`queue.service.ts`), NON il
      // container di produzione.
      extraOptions: { idempotente: true },
      invalidatesTags: ['Queue'],
    }),

    /**
     * The caller's live matchmaking state (GET /v1/queue/status). Consumed with
     * `pollingInterval ≈ 3000ms` ONLY while the QueueSheet is open + searching;
     * the sheet flips to "found" when `currentMatch != null`.
     */
    queueStatus: builder.query<QueueStatus, void>({
      query: () => '/v1/queue/status',
      providesTags: ['Queue'],
    }),

    /**
     * Every tournament ladder the caller is enrolled in (GET /v1/leagues?sub=).
     *
     * `sub` is mandatory and the server only lets you read your OWN (403
     * `forbidden_sub` otherwise), so this is a self-query: pass the sub decoded
     * from our own access token. Returns per-server leagues AND the global one —
     * they coexist, a player can be in several at once.
     *
     * Gated server-side by FEATURE_LEADERBOARD: with the flag off the route is
     * not mounted and this 404s, which the caller treats as "no ladders".
     */
    getMyLadders: builder.query<QduelsUserLeague[], string>({
      query: (sub) => `/v1/leagues?sub=${encodeURIComponent(sub)}`,
      transformResponse: (raw: unknown): QduelsUserLeague[] => {
        const r = (raw ?? {}) as { leagues?: unknown };
        return Array.isArray(r.leagues) ? (r.leagues as QduelsUserLeague[]) : [];
      },
      providesTags: ['Ladder'],
    }),

    /**
     * One ladder's ranking (GET /v1/leaderboard).
     *
     * `guildId` is REQUIRED for discord/telegram and REJECTED for global — the
     * server refuses a contradictory pair rather than ignoring it, so we send it
     * only when it belongs. 404 means "no league for that server-month yet",
     * which is a normal state, not an error.
     */
    getLadder: builder.query<
      QduelsLadder,
      { platform: string; guildId?: string; month?: string; limit?: number }
    >({
      query: ({ platform, guildId, month, limit = 50 }) => {
        const p = new URLSearchParams({ platform, limit: String(limit) });
        if (platform !== 'global' && guildId) p.set('guildId', guildId);
        if (month) p.set('month', month);
        return `/v1/leaderboard?${p.toString()}`;
      },
      providesTags: ['Ladder'],
    }),

    /**
     * The caller's ranked standing (GET /v1/rating). Personal only — there is
     * no endpoint that orders players by XP, so this never becomes a ladder.
     *
     * The route is mounted unconditionally server-side and answers 200 with
     * `{enabled:false, xp:null, …}` when the flag is off, so a disabled rating
     * is DATA, not an error: the circle reads `enabled` and stands down. Kept
     * out of the queue tag on purpose — a queue poll every 2s would refetch
     * this needlessly; it only moves when a match settles.
     */
    getRating: builder.query<QduelsRating, void>({
      query: () => '/v1/rating',
      providesTags: ['Rating'],
    }),

    // ─── Match detail + post-match stats ─────────────────────────────────

    /**
     * A single match by its CR game tag (GET /v1/matches/:gameTag). Drives the
     * DuelResult header (winner / score / mode). Returned unwrapped. A 404
     * `{ error: "match_not_found" }` means the tag isn't known (yet).
     */
    getMatch: builder.query<MatchDTO, string>({
      query: (gameTag) => `/v1/matches/${encodeURIComponent(gameTag)}`,
      keepUnusedDataFor: 300,
    }),

    /**
     * Post-match stats + deck deep links for a completed match
     * (GET /v1/matches/:gameTag/stats). Drives the DuelResult per-player blocks
     * (decks + rich stats), attributed to you vs opponent by the `tag` each
     * `stats`/`copyDecks` entry carries. A 404 `{ error: "stats_not_found" }`
     * means the match has no computed stats (not complete / replay missing).
     */
    getMatchStats: builder.query<MatchStatsDTO, string>({
      query: (gameTag) => `/v1/matches/${encodeURIComponent(gameTag)}/stats`,
      keepUnusedDataFor: 300,
    }),

    // ─── Tournaments ─────────────────────────────────────────────────────

    /**
     * Discovery: `visibility: 'public'` tournaments, independent of the caller —
     * THE only list a user can browse without having joined; everything else is
     * caller-scoped or id-addressed.
     *
     * `status` picks which public list: omitted / `'registration'` = the
     * joinable list (default), `'in_progress'` = the LIVE list (public
     * tournaments being played now — how a non-participant finds a match to
     * watch). Both are keyset-paged with the same shape.
     *
     * ⚠️ Do NOT pass `role` (with `scope=open` qduels REJECTS it, 400), and do
     * NOT pass `status: 'complete'|'cancelled'` (also 400 — not browsable). The
     * type only permits the two browsable values. There is no `kind` filter —
     * split standard vs sit&go client-side on the `kind` field.
     *
     * Rate-limited 60/min per user (it is an enumeration surface).
     */
    listOpenTournaments: builder.query<QduelsOpenTournamentsPage, ListOpenTournamentsQuery | void>({
      query: (params) => ({
        url: '/v1/tournaments',
        params: {
          scope: 'open',
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.limit != null ? { limit: params.limit } : {}),
          ...(params?.cursor ? { cursor: params.cursor } : {}),
        },
      }),
      providesTags: ['Tournaments'],
    }),

    /**
     * The caller's own tournaments (default scope) — organizer and/or
     * participant, never a global list. A brand-new user gets `{tournaments:
     * []}` with HTTP 200, which is normal, not an error.
     *
     * This is what backs the Community list: filter client-side for
     * `guildId !== null` (a channel-bound tournament the user joined via that
     * channel's bot).
     */
    listMyTournaments: builder.query<QduelsMyTournamentsResponse, ListMyTournamentsQuery | void>({
      query: (params) => ({
        url: '/v1/tournaments',
        params: params
          ? {
              ...(params.role ? { role: params.role } : {}),
              ...(params.status ? { status: params.status } : {}),
              ...(params.limit != null ? { limit: params.limit } : {}),
            }
          : undefined,
      }),
      providesTags: ['Tournaments'],
    }),

    /**
     * A single tournament + slot rollup. NOT caller-scoped: any authenticated
     * user can read any tournament by id, so a shared/deep link renders even
     * for someone who never joined.
     */
    getTournament: builder.query<QduelsTournamentDetail, string>({
      query: (tournamentId) => `/v1/tournaments/${encodeURIComponent(tournamentId)}`,
      providesTags: (_r, _e, id) => [{ type: 'Tournaments' as const, id }],
    }),

    /**
     * Bracket slots. Defaults to `round = currentRound`; there is no "all
     * rounds" (round=0 is rejected), so a full bracket costs one request per
     * round.
     *
     * ⚠️ `playerTag` does NOT simply filter: it **cancels the currentRound
     * default** and flips the sort to `{slot: 1}` ascending. Since slots are
     * numbered globally across the bracket (round 1 = slots 1..size/2), the
     * first row you get back is the player's ROUND-1 slot — not their current
     * one. Always pass `round` explicitly alongside `playerTag`, or you will
     * read a finished slot and tell a live player their round is over.
     * Paginate a playerTag/fromSlot page with `nextFromSlot`, never `cursor`.
     */
    getTournamentSlots: builder.query<
      QduelsTournamentSlotsPage,
      {
        tournamentId: string;
        round?: number;
        playerTag?: string;
        limit?: number;
        fromSlot?: number;
      }
    >({
      query: ({ tournamentId, round, playerTag, limit, fromSlot }) => ({
        url: `/v1/tournaments/${encodeURIComponent(tournamentId)}/slots`,
        params: {
          ...(round != null ? { round } : {}),
          ...(playerTag ? { playerTag } : {}),
          ...(limit != null ? { limit } : {}),
          ...(fromSlot != null ? { fromSlot } : {}),
        },
      }),
      providesTags: (_r, _e, { tournamentId }) => [
        { type: 'Tournaments' as const, id: tournamentId },
      ],
    }),

    /**
     * Roster. Open to any authenticated caller (like the bracket).
     *
     * `playerTag` filters to that single participant — the O(1) "am I in?"
     * check. Use it for membership instead of scanning a page: the roster is
     * capped (server page limit 50) while a bracket can hold up to 1024, so a
     * client-side scan silently reports "not joined" for anyone past the first
     * page.
     */
    getTournamentParticipants: builder.query<
      QduelsTournamentParticipantsPage,
      { tournamentId: string; playerTag?: string; limit?: number; cursor?: string }
    >({
      query: ({ tournamentId, playerTag, limit, cursor }) => ({
        url: `/v1/tournaments/${encodeURIComponent(tournamentId)}/participants`,
        params: {
          ...(playerTag ? { playerTag } : {}),
          ...(limit != null ? { limit } : {}),
          ...(cursor ? { cursor } : {}),
        },
      }),
      providesTags: (_r, _e, { tournamentId }) => [
        { type: 'Tournaments' as const, id: tournamentId },
      ],
    }),

    /**
     * Final standings — only meaningful once `complete`. 409
     * `tournament_not_complete` while it's still running, so the caller gates
     * the query on status. `standings: []` = completed with no champion.
     * Placements are shared on ties and NOT consecutive — group by `placement`.
     */
    getTournamentStandings: builder.query<QduelsTournamentStandings, string>({
      query: (tournamentId) => `/v1/tournaments/${encodeURIComponent(tournamentId)}/standings`,
      providesTags: (_r, _e, id) => [{ type: 'Tournaments' as const, id }],
    }),

    /**
     * Join. Gated twice beyond auth: the caller must be CR-linked (400
     * `player_not_linked`) AND pass a LIVE eligibility check against Supercell
     * (403 `not_eligible`) — which is why this can also fail with Supercell's
     * own transient errors (`cr_player_not_found` 404 / `cr_rate_limited` 503 /
     * `cr_upstream_error` 502). Treat those as infrastructure, NOT as "the
     * tournament is gone".
     *
     * Rate-limited 30/min. Returns an ad-hoc object, not a TournamentSummary.
     */
    joinTournament: builder.mutation<QduelsTournamentJoinResult, string>({
      query: (tournamentId) => ({
        url: `/v1/tournaments/${encodeURIComponent(tournamentId)}/join`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => ['Tournaments', { type: 'Tournaments' as const, id }],
    }),

    /** Leave — only during `registration`, else `cannot_leave_<status>`. */
    leaveTournament: builder.mutation<QduelsTournamentLeaveResult, string>({
      query: (tournamentId) => ({
        url: `/v1/tournaments/${encodeURIComponent(tournamentId)}/leave`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => ['Tournaments', { type: 'Tournaments' as const, id }],
    }),

    /**
     * "I'm here" — the check-in gate. The in-game invite is only sent once BOTH
     * players confirm, so this is what actually creates the match.
     *
     * The server derives your slot from the token: no slot number needed.
     * **Idempotent**, even after both sides flipped — a double tap is a 200, so
     * there is no error path to render for it.
     *
     * Only reachable while a slot is `awaiting_checkin`. With qduels'
     * `FEATURE_MATCH_CHECKIN` gate off this answers `409 checkin_not_open` —
     * which is also why the UI keys off the slot state instead of a client flag.
     */
    checkInTournament: builder.mutation<QduelsTournamentCheckinResult, string>({
      query: (tournamentId) => ({
        url: `/v1/tournaments/${encodeURIComponent(tournamentId)}/checkin`,
        method: 'POST',
      }),
      // 🔴 ECCEZIONE DICHIARATA (`lib/ritentaSoloLeLetture.ts`): si ritenta UNA volta, su rete/timeout/5xx.
      // PERCHE': il ritentativo risparmia UN TOCCO al giocatore, non salva una finestra. La schermata del torneo l'errore lo
      // MOSTRA (`DuelTournamentDetail.tsx:256-267`) e la finestra e' a tempo (410 `checkin_expired`) ma di 5 minuti di
      // default (`TOURNAMENT_CHECKIN_WINDOW_MIN`, `qduels-api/src/config.ts:115`): un guasto passeggero al primo tocco non la
      // fa scadere, si riprova a mano. Sul server e' indicizzata su (utente, torneo) ed e' idempotente (un doppio tocco e' un
      // 200); in ritardo 410/409, o 200 di replica. Limite 30/min, CONDIVISO con `joinTournament`.
      // ⛔ COSTO [D dal codice, non misurato]: una chiamata rimasta indietro puo' confermare lo slot del turno SUCCESSIVO.
      // Fonte: clone locale di QDuels @de2f7a79, letto il 30/09/2026 (`tournament.service.ts`), NON la produzione.
      extraOptions: { idempotente: true },
      invalidatesTags: (_r, _e, id) => [{ type: 'Tournaments' as const, id }],
    }),

    /** Same gate, queue side. The server derives your pair from the token. */
    checkInQueue: builder.mutation<QduelsQueueCheckinResult, void>({
      query: () => ({ url: '/v1/queue/checkin', method: 'POST' }),
      // 🔴 ECCEZIONE DICHIARATA (`lib/ritentaSoloLeLetture.ts`): si ritenta UNA volta, su rete/timeout/5xx.
      // PERCHE': se il tocco fallisce non resta niente a schermo (`QueueSheet.tsx`, `catch` vuoto per scelta) e la
      // finestra a tempo scade. Sul server e' indicizzata sull'utente ed e' idempotente (scrittura protetta su
      // `checkedInAt: null`); in ritardo 410 `checkin_expired` o 409 `checkin_not_open`.
      // ⛔ COSTO [D dal codice, non misurato]: una chiamata rimasta indietro puo' confermare la coppia SUCCESSIVA se nel
      // frattempo il giocatore e' stato riabbinato. Fonte: clone locale di QDuels @de2f7a79, letto il 30/09/2026
      // (`queue.service.ts`), NON la produzione.
      extraOptions: { idempotente: true },
      invalidatesTags: ['Queue'],
    }),
  }),
});

export const {
  useCreateChallengeMutation,
  useListChallengesQuery,
  useGetChallengeQuery,
  useAcceptChallengeMutation,
  useDeclineChallengeMutation,
  useCancelChallengeMutation,
  useGetMyLinkQuery,
  useVerifyTokenMutation,
  usePlayerProfileQuery,
  useGetPlayerByCrTagQuery,
  useQueueEnterMutation,
  useQueueLeaveMutation,
  useQueueStatusQuery,
  useGetRatingQuery,
  useGetMyLaddersQuery,
  useGetLadderQuery,
  useGetMatchQuery,
  useGetMatchStatsQuery,
  useListOpenTournamentsQuery,
  useListMyTournamentsQuery,
  useGetTournamentQuery,
  useGetTournamentSlotsQuery,
  useGetTournamentParticipantsQuery,
  useJoinTournamentMutation,
  useLeaveTournamentMutation,
  useCheckInTournamentMutation,
  useCheckInQueueMutation,
  useGetTournamentStandingsQuery,
} = qduelsApi;
